// reCAPTCHA v3 (invisible) helper.
//
// Loads the Google reCAPTCHA v3 script once and returns a fresh token for a
// given action. If VITE_RECAPTCHA_V3_SITE_KEY is not configured, this is a
// no-op (resolves to null) so forms keep working in dev / before keys are set
// -- the backend's verifyCaptchaV3 middleware fails open in the same way.

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '';

// Shown when reCAPTCHA is configured but produced no token — almost always an
// ad/tracker blocker stopping Google's script. Forms that the server checks
// strictly (contact, message notifications) can't be sent without it.
export const RECAPTCHA_BLOCKED_MESSAGE =
  "We couldn't run our spam check. Please turn off your ad blocker (or allow google.com) for this site, reload the page and try again.";

// True when a strict form should stop: reCAPTCHA is on but no token came back.
export function recaptchaBlocked(token) {
  return !!SITE_KEY && !token;
}

let scriptPromise = null;

function loadScript() {
  if (!SITE_KEY) return Promise.resolve(null);
  if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve(window.grecaptcha);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Start loading the reCAPTCHA script ahead of time (e.g. when a form opens) so
 * the submit click doesn't pay for the script download.
 */
export function preloadRecaptcha() {
  loadScript().catch(() => {});
}

// grecaptcha.ready()/execute() have no failure callback of their own; a script
// that half-loads (blocker, flaky network) would otherwise hang the form.
const TOKEN_TIMEOUT_MS = 5000;

/**
 * Get a reCAPTCHA v3 token for the given action.
 * @param {string} action - logical action name (e.g. 'contact', 'report')
 * @returns {Promise<string|null>} token, or null if reCAPTCHA isn't configured
 */
export async function getRecaptchaToken(action = 'submit') {
  if (!SITE_KEY) return null;
  try {
    const grecaptcha = await loadScript();
    if (!grecaptcha) return null;
    return await new Promise((resolve) => {
      setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
      grecaptcha.ready(() => {
        grecaptcha.execute(SITE_KEY, { action }).then(resolve).catch(() => resolve(null));
      });
    });
  } catch (e) {
    // Network/script failure -- let the request proceed; backend decides.
    return null;
  }
}
